import { Space } from "@/atoms"
import { Heading } from "@/molecules/Heading"
import { SourceList } from "@/organisms/Drawers/components/SourceList"
import { findingsToSources } from "@/organisms/Drawers/components/SourceList/utils"
import { Item, List } from "@/pages/DiscoveryPage/components/molecules/List"
import { isDockerfileFix } from "../DockerfileImageFixes"

import type {
  ApplicativeImageFinding,
  CodeRepoFinding,
  ImagePackageFix
} from "@/domain/issue"
import type { BaseCardComponentProps } from "../../../RemediationGuide.types"

import styles from "./PackageUpdates.module.scss"
import { getUniqueImagePackageFixes } from "./utils"

export const PackageUpdates: React.FC<
  BaseCardComponentProps & {
    codeRepoFindings: CodeRepoFinding[]
    imageFindings: ApplicativeImageFinding[]
    hasCodeRepoFixes: boolean
    showSources: boolean
    copyable?: boolean
  }
> = ({
  codeRepoFindings,
  imageFindings,
  t,
  tKey,
  copyable,
  hasCodeRepoFixes,
  showSources
}) => {
  const uniqueFixes = getUniqueImagePackageFixes(imageFindings)
  const nonDockerfileFixes = uniqueFixes.filter((fix) => !isDockerfileFix(fix))
  const packageUpdateFixes = nonDockerfileFixes.filter((fix) => fix.packageName)
  const hasImagePackageFixes = packageUpdateFixes.length > 0
  const sources = findingsToSources(codeRepoFindings)
  const fixesCount =
    packageUpdateFixes.length +
    sources.reduce((acc, source) => acc + (source.remedies?.length || 0), 0)

  if (!hasImagePackageFixes && !hasCodeRepoFixes && !showSources) return null

  return (
    <Space orientation="vertical" className={styles.container}>
      <Heading
        level={6}
        title={
          hasImagePackageFixes || hasCodeRepoFixes
            ? `${t(`${tKey}updatePackage`, { count: fixesCount })}:`
            : `${t(`${tKey}packageUpdate`)}:`
        }
      />
      {hasImagePackageFixes && (
        <List
          items={packageUpdateFixes}
          render={({ packageName, fixType }: ImagePackageFix) => {
            return (
              <Item
                key={packageName + fixType}
                value={""}
                title={t(`general.${fixType}Update`)}
              />
            )
          }}
        />
      )}
      {showSources && (
        <SourceList sources={sources} type="remedies" noLimits={!!copyable} />
      )}
    </Space>
  )
}
